/**
 * Forgot Password Form component for trAInedup.ai
 * Allows users to request a password reset email
 */

import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { Card } from '../common/Card';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';

/**
 * Forgot password form component with validation and success state
 */
export const ForgotPasswordForm: React.FC = () => {
  const { resetUserPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const requestedReturnTo = searchParams.get('returnTo') || '';
  const safeReturnTo = requestedReturnTo.startsWith('/') ? requestedReturnTo : '';
  const loginPath = safeReturnTo || ROUTES.login;

  const resetUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/reset-password${safeReturnTo ? `?returnTo=${encodeURIComponent(safeReturnTo)}` : ''}`
      : undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await resetUserPassword(email, resetUrl);
      if (result.success) {
        setSuccessMessage('Check your inbox for a link to reset your password.');
      } else {
        setError(result.error || 'Unable to send reset email. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Unable to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <div className="text-center mb-6">
        <h2 className="font-montserrat font-bold text-2xl text-neutral-800 mb-2">Reset your password</h2>
        <p className="text-neutral-600 text-sm">
          Enter the email associated with your account and we&apos;ll send you a reset link.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 text-sm">{successMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email Address"
          name="email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) {
              setError('');
            }
          }}
          icon={Mail}
          placeholder="Enter your email"
          required
          disabled={isLoading}
          fullWidth
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? 'Sending link...' : 'Send reset link'}
        </Button>
      </form>

      <div className="mt-6 flex items-center justify-center">
        <ArrowLeft className="w-4 h-4 text-neutral-500 mr-2" />
        <Link
          to={loginPath}
          className="text-sm text-primary hover:text-primary-600 font-medium transition-colors duration-200"
        >
          Back to sign in
        </Link>
      </div>
    </Card>
  );
};

export default ForgotPasswordForm;
